import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

// Actions
import {
    GetFieldLabels,
    GetFieldLabelTypes,
    EditFieldLabel,
    EditFieldLabelType,
} from "../../../actions/fieldLabelsActions";
import { showModal } from "../../../actions/actionCreators";
import { notifyError } from "../../../actions/notifyActions";
// Custom Components
import FieldLabelAdd from "./FieldLabelAdd";

// Custom Material Components
import TableHeader from "../../material-components/Table/TableHeader";
import TableBody from "../../material-components/Table/TableBody";
import TableFooter from "../../material-components/Table/TableFooter";

// Material UI
import TextField from "@material-ui/core/TextField";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";

// Stores
import { IAppState } from "../../../types/stores";
import { IFieldLabel } from "../../../types/stores/fieldTypes";

const FieldLabelsMain: React.FC<{}> = () => {
    const dispatch = useDispatch();

    // State
    // const [order, setOrder] = useState("asc");
    // const [orderBy, setOrderBy] = useState("FieldLabelName");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [stateLabels, setStateLabels] = useState<IFieldLabel[]>([]);

    // Props
    const labels = useSelector((state: IAppState) => state.fieldLabels.labels);
    const labelTypes = useSelector((state: IAppState) => state.fieldLabels.labelTypes);
    const roles = useSelector((state: IAppState) => state.session.roles);

    useEffect(() => {
        dispatch(GetFieldLabels());
    }, [dispatch]);

    useEffect(() => {
        dispatch(GetFieldLabelTypes());
    }, [dispatch]);

    useEffect(() => {
        if (labels) {
            setStateLabels(labels);
        }
    }, [labels]);

    const handleNameChanges = (id: number, newValue: string) => {
        if (!stateLabels || (stateLabels && stateLabels.length <= 0)) return;

        const labelsCopy = [...stateLabels].map(x => ({ ...x }));
        let index = labelsCopy.findIndex(item => item.FieldLabelId === id);
        let labelToChange = labelsCopy[index];

        if (labelToChange) {
            labelToChange.FieldLabelName = newValue;
            labelsCopy[index] = labelToChange;
            setStateLabels(labelsCopy);
        }
    };

    const handleNameSaves = (id: number, newValue: string) => {
        if (!stateLabels || (stateLabels && stateLabels.length <= 0)) return;

        const labelsOriginal = [...labels].map(x => ({ ...x }));

        if (!newValue) {
            setStateLabels(labelsOriginal);
            showEmptyError();
            return;
        }

        const labelsCopy = [...stateLabels].map(x => ({ ...x }));
        const alreadyExists = labelsCopy.some(
            item => item.FieldLabelName.toUpperCase() == newValue.toUpperCase() && item.FieldLabelId != id
        );

        if (alreadyExists) {
            showConfirm(newValue);
        } else {
            dispatch(EditFieldLabel(id, newValue));
        }
    };

    const showEmptyError = () => {
        let modalText = "Name cannot be empty";
        dispatch(notifyError(modalText));
    };

    const showConfirm = (newValue: string) => {
        let modalText = `There is already a field label name ${newValue}. Please create a different name.`;

        dispatch(notifyError(modalText));
    };

    const handleTypeSaves = (id: number, newValue: number) => {
        if (!stateLabels || (stateLabels && stateLabels.length <= 0)) return;

        const labelsCopy = [...stateLabels].map(x => ({ ...x }));
        let index = labelsCopy.findIndex(item => item.FieldLabelId === id);
        let labelToChange = labelsCopy[index];

        if (labelToChange) {
            labelToChange.FieldLabelTypeId = newValue;
            labelsCopy[index] = labelToChange;
            setStateLabels(labelsCopy);
            dispatch(EditFieldLabelType(id, newValue));
        }
    };

    const handleNameSavesEnter = (id, e) => {
        if (e.nativeEvent.code == "Enter") {
            handleNameSaves(id, e.target.value);
            e.target.blur();
        }
    };

    const renderNameActionColumn = (id: number, value: string) => (
        <TextField
            value={value ? value : ""}
            onChange={e => handleNameChanges(id, e.target.value)}
            onBlur={e => handleNameSaves(id, e.target.value)}
            onKeyDown={e => handleNameSavesEnter(id, e)}
        />
    );

    const renderTypeActionColumn = (id: number, value: number | null) => (
        <Select
            value={value}
            onChange={e => handleTypeSaves(id, e.target.value as number)}
            style={{ width: "100%", textAlign: "left" }}
        >
            {labelTypes.map(x => (
                <MenuItem key={`${"sv"}${x.LabelTypeId}`} value={x.LabelTypeId}>
                    {x.LabelTypeName}
                </MenuItem>
            ))}
        </Select>
    );

    const renderNewActionColumn = (
        value: number,
        canDelete: boolean,
        labelName: string,
        isNeedApprovalLabel: boolean | undefined
    ) => {
        const isAdmin = roles.includes("Super Admin") || roles.includes("Experian Admin");
        const showApprovalConfigButton = isAdmin && isNeedApprovalLabel;

        const deleteButton = canDelete && (
            <Tooltip title="Delete">
                <IconButton id="btn-delete" color="secondary" onClick={() => renderConfirmDelete(value)} size="small">
                    <i className={"material-icons"} style={{ height: "20px", width: "20px", fontSize: "20px" }}>
                        delete
                    </i>
                </IconButton>
            </Tooltip>
        );

        const approvalButton = showApprovalConfigButton && (
            <Tooltip title="Approval Email Settings">
                <IconButton
                    id="btn-approval-settings"
                    color="primary"
                    onClick={() => renderApprovalSettings(value, labelName)}
                    size="small"
                >
                    <i className={"material-icons"} style={{ height: "20px", width: "20px", fontSize: "20px" }}>
                        settings
                    </i>
                </IconButton>
            </Tooltip>
        );

        return (
            <div>
                {approvalButton}
                {deleteButton}
            </div>
        );
    };

    const renderConfirmDelete = (id: number) => {
        dispatch(showModal("FIELD_LABEL_DELETE", { id }));
    };

    const renderApprovalSettings = (fieldLabelId: number, labelName: string) => {
        dispatch(showModal("FIELD_LABEL_APPROVAL", { fieldLabelId, labelName }));
    };

    // const handleRequestSort = (event, property) => {
    //     if (order === "asc") {
    //         setOrder("desc");
    //     } else {
    //         setOrder("asc");
    //     }

    //     setOrderBy(property);
    // };

    let tableHeader = [
        ["FieldLabelName", "Name", "smDown"],
        ["FieldLabelTypeId", "Type", "smDown", "left"],
        ["Actions", "Actions", "smDown"],
    ];

    let data: any[] = [];
    const tableData = stateLabels || [];
    const sortedData = tableData;
    const needsApprovalType = labelTypes.find(x => x.LabelTypeName == "NEEDS_APPROVAL");
    //const sortedData = stableSort(tableData, getSorting(order, orderBy));
    sortedData.map(dataRow => {
        const isNeedApprovalLabel = needsApprovalType && dataRow.FieldLabelTypeId == needsApprovalType.LabelTypeId;
        data.push([
            ["FieldLabelName", renderNameActionColumn(dataRow.FieldLabelId, dataRow.FieldLabelName), "smDown"],
            [
                "FieldLabelTypeId",
                renderTypeActionColumn(dataRow.FieldLabelId, dataRow.FieldLabelTypeId),
                "smDown",
                "left",
            ],
            [
                "Actions",
                renderNewActionColumn(
                    dataRow.FieldLabelId,
                    dataRow.CanDelete,
                    dataRow.FieldLabelName,
                    isNeedApprovalLabel
                ),
                "smDown",
            ],
        ]);
    });

    const emptyMessage = ["EmptyMessage", "No Data to Display.", "smDown"];

    return (
        <div>
            <Paper>
                <Table size="medium">
                    <TableHeader
                        headers={tableHeader} /*orderBy={orderBy} order={order} onClick={handleRequestSort}*/
                    />
                    <TableBody data={data} page={page} rowsPerPage={rowsPerPage} emptyMessage={emptyMessage} />
                </Table>
                <TableFooter
                    displayDense={false}
                    displayPaging={true}
                    rowsPerPageOptions={[5, 10, 15]}
                    count={tableData.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(_, newPage) => {
                        setPage(newPage);
                    }}
                    onRowsPerPageChange={e => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                />
            </Paper>
            <h2 className="MuiTypography-root MuiTypography-h6" style={{ padding: "10px 0px" }}>
                Add New Label
            </h2>
            <Paper style={{ padding: "0px 10px 10px 10px" }}>
                <FieldLabelAdd />
            </Paper>
        </div>
    );
};

export default FieldLabelsMain;
